/**
 * Daten-Import mit Merge-Review — the React port of app/web/import.html.
 *
 * The flow of the original page is kept exactly, because it is the mental model
 * the users already have: upload a CSV → the server DIFFS it against the CMDBuild
 * stock and writes nothing → every changed entry can individually be taken over
 * ("Übernehmen") or left alone ("Behalten") → apply.
 *
 * What the port had to get right beyond the markup:
 *   - The decision map is transmitted IN FULL for every non-'unchanged' entry. A
 *     missing entry_id defaults to 'incoming' server-side, so a partial map would
 *     silently import what the user rejected.
 *   - Plans live in an in-process store with a 30-minute TTL, so `apply` can
 *     answer 404 ("Plan nicht (mehr) vorhanden — bitte CSV erneut hochladen…").
 *     That message is shown verbatim and paired with a one-click path back: the
 *     picked File is kept in state, so the same file can simply be re-compared.
 *     The page also warns BEFORE the user tries, once the TTL has elapsed.
 *   - Unknown classes cannot vanish: known classes render in schema order, then
 *     anything else the server sent, under its raw name (`ImportKlassLoose`).
 *   - Import is a write action. The route already requires `import.write`, but the
 *     component checks it again and disables every control — an auditor sees the
 *     explanation instead of a dead button.
 */

import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Banner } from '../components/Banner';
import { Chip, ChipList } from '../components/Chip';
import { Icon } from '../components/Icon';
import { KpiRow } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PermissionDenied } from '../components/RequirePermission';
import Spinner from '../components/Spinner';
import { useToast } from '../components/Toast';
import { ApiError, applyImport, errorMessage, fetchCmdbCard, planImport } from '../lib/api';
import { useAuth } from '../lib/auth';
import { uniqueStrings } from '../lib/format';
import { intlLocale } from '../lib/locale';
import { IMPORT_KLASS_LABELS, IMPORT_KLASS_ORDER } from '../lib/types';
import type {
  ImportApplyResult,
  ImportDecision,
  ImportPlan,
  ImportTargetRequest,
  InstanceVersionChoice,
  KpiCounter,
  PlanChange,
  PlanEntry,
} from '../lib/types';

import './admin/ops.css';
import { BomReview } from './import/BomReview';
import { ImportTargetPanel, type ProductTargetState } from './import/ImportTargetPanel';
import { TargetSummary } from './import/TargetSummary';
import { PlanDecision } from './import/PlanDecision';
import { buildPlanPresentation, type EntryPresentation } from './import/planPresentation';
import './import/import-review.css';

/** Server-side plan TTL (app/services/import_service.py). */
const PLAN_TTL_MS = 30 * 60 * 1000;

/** How often the "plan expires at" hint is re-evaluated. */
const TICK_MS = 15_000;

function klassLabel(klass: string): string {
  const labels: Readonly<Record<string, string | undefined>> = IMPORT_KLASS_LABELS;
  return labels[klass] ?? klass;
}

const ACTION_SYMBOL: Readonly<Record<string, string>> = {
  create: '+',
  update: '~',
  unchanged: '=',
  remove: '−',
};

const ACTION_CLASS: Readonly<Record<string, string>> = {
  create: 'add',
  update: 'mod',
  unchanged: 'same',
  remove: 'del',
};

/**
 * Starting decisions: take the file for creates/updates (the server default),
 * KEEP components the scan lacks, and leave ambiguous matches undecided — the
 * server refuses the apply until the operator picked one. Catalog entries that
 * follow the Stückliste carry no decision of their own.
 */
function initialDecisions(plan: ImportPlan): Record<string, ImportDecision> {
  const undecided = new Set(plan.requires_decision ?? []);
  const next: Record<string, ImportDecision> = {};
  for (const entry of plan.entries) {
    if (entry.action === 'unchanged' || undecided.has(entry.entry_id)) continue;
    if ((entry.display?.follows?.length ?? 0) > 0) continue;
    next[entry.entry_id] = entry.action === 'remove' ? 'existing' : 'incoming';
  }
  return next;
}

function isChange(decision: ImportDecision): boolean {
  return decision === 'incoming' || decision.startsWith('match:');
}

/** One field-level diff line — old struck through, new highlighted. */
function DiffLine({ change }: { change: PlanChange }): ReactNode {
  return (
    <div className="diff">
      <span className="f">{change.field}:</span>{' '}
      {change.old === null ? (
        <span className="new">{change.new}</span>
      ) : (
        <>
          <span className="visually-hidden"><Trans>Bisher:</Trans>{' '}</span>
          <del className="old">{change.old}</del>
          <Icon name="arrow-right" size={12} className="arr" />
          <span className="visually-hidden"><Trans>Neu:</Trans>{' '}</span>
          <span className="new">{change.new}</span>
        </>
      )}
    </div>
  );
}

interface PlanEntryItemProps {
  entry: PlanEntry;
  presentation: EntryPresentation | undefined;
  /** null for 'unchanged' entries, which carry no decision. */
  decision: ImportDecision | null;
  /** All decisions — entries that follow the Stückliste derive their state from them. */
  decisions: Readonly<Record<string, ImportDecision>>;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

/** Explains a catalog entry that is created together with its Stückliste entry. */
function FollowsNote({ entry, created }: { entry: PlanEntry; created: boolean }): ReactNode {
  const type = entry.klass === 'KomponenteType';
  // One whole message per case, so a translation never stitches fragments.
  let note: ReactNode;
  if (type && created) {
    note = (
      <Trans>
        Neue Komponente aus dem Scan. Sie wird im Komponentenkatalog nur angelegt, wenn Sie eine ihrer
        Versionen unten unter »Stücklisten« übernehmen.
      </Trans>
    );
  } else if (type) {
    note = (
      <Trans>
        Neue Komponente aus dem Scan. Sie wird im Komponentenkatalog nur angelegt, wenn Sie eine ihrer
        Versionen unten unter »Stücklisten« übernehmen. Dort sind derzeit alle ihre Versionen übersprungen.
      </Trans>
    );
  } else if (created) {
    note = (
      <Trans>
        Neue Komponentenversion aus dem Scan. Sie wird im Komponentenkatalog nur angelegt, wenn Sie sie
        unten unter »Stücklisten« übernehmen.
      </Trans>
    );
  } else {
    note = (
      <Trans>
        Neue Komponentenversion aus dem Scan. Sie wird im Komponentenkatalog nur angelegt, wenn Sie sie
        unten unter »Stücklisten« übernehmen. Dort ist sie derzeit übersprungen.
      </Trans>
    );
  }
  return <p className="imp-follows-note">{note}</p>;
}

function PlanEntryItem({ entry, presentation, decision, decisions, disabled, onDecide }: PlanEntryItemProps): ReactNode {
  const { t } = useLingui();
  const follows = entry.display?.follows ?? [];
  const followsCreated = follows.some((id) => decisions[id] === 'incoming');
  const classes = ['entry', 'imp-entry', ACTION_CLASS[entry.action] ?? 'same'];
  if (decision === 'existing' || (follows.length > 0 && !followsCreated)) classes.push('skipped');
  const title = presentation?.title ?? entry.key;
  const changes = presentation?.changes ?? entry.changes;
  const version = presentation?.version;

  return (
    <div className={classes.join(' ')}>
      <div className="sym" aria-hidden="true">
        {ACTION_SYMBOL[entry.action] ?? '?'}
      </div>
      <div className="body">
        <div className="imp-entry-title">
          <span className="key">{title}</span>
          {version ? <Chip className="imp-version"><Trans>Version {version}</Trans></Chip> : null}
        </div>
        {entry.action === 'unchanged' ? null : (
          <div className="diffs">
            {changes.length === 0 ? (
              <div className="diff">
                <span className="f"><Trans>keine Feldänderungen gemeldet</Trans></span>
              </div>
            ) : (
              changes.map((change, index) => (
                <DiffLine key={`${change.field}-${index}`} change={change} />
              ))
            )}
          </div>
        )}
        {follows.length > 0 ? <FollowsNote entry={entry} created={followsCreated} /> : null}
      </div>
      {follows.length > 0 ? (
        <Chip className="imp-follows" tone={followsCreated ? 'good' : 'neutral'}>
          {followsCreated ? t`Wird mit der Stückliste angelegt` : t`Wird nicht angelegt`}
        </Chip>
      ) : (
        <PlanDecision
          entry={entry}
          label={version ? t`${title} · Version ${version}` : title}
          decision={decision}
          disabled={disabled}
          onDecide={onDecide}
        />
      )}
    </div>
  );
}

/** Counts of a finished import — one whole sentence per combination. */
function ResultSummary({ result }: Readonly<{ result: ImportApplyResult }>): ReactNode {
  const created = result.created.length;
  const updated = result.updated.length;
  const removed = result.removed?.length ?? 0;
  const skipped = result.skipped.length;
  const errorCount = result.errors.length;
  if (removed > 0 && errorCount > 0) {
    return (
      <Trans>
        Import abgeschlossen: <b>{created}</b> angelegt, <b>{updated}</b> aktualisiert, <b>{removed}</b> entfernt,{' '}
        {skipped} behalten/übersprungen, <b><Plural value={errorCount} one="# Fehler" other="# Fehler" /></b>.
      </Trans>
    );
  }
  if (removed > 0) {
    return (
      <Trans>
        Import abgeschlossen: <b>{created}</b> angelegt, <b>{updated}</b> aktualisiert, <b>{removed}</b> entfernt,{' '}
        {skipped} behalten/übersprungen.
      </Trans>
    );
  }
  if (errorCount > 0) {
    return (
      <Trans>
        Import abgeschlossen: <b>{created}</b> angelegt, <b>{updated}</b> aktualisiert, {skipped} behalten/übersprungen,{' '}
        <b><Plural value={errorCount} one="# Fehler" other="# Fehler" /></b>.
      </Trans>
    );
  }
  return (
    <Trans>
      Import abgeschlossen: <b>{created}</b> angelegt, <b>{updated}</b> aktualisiert, {skipped} behalten/übersprungen.
    </Trans>
  );
}

/** What happened to the uploaded instance's version. */
function InstanceResultNote({ instance }: Readonly<{ instance: NonNullable<ImportApplyResult['instance']> }>): ReactNode {
  const versionNr = instance.version_nr;
  if (instance.action === 'unchanged') {
    return <Trans>Die Produktinstanz bleibt auf Version <b>{versionNr}</b>.</Trans>;
  }
  if (instance.action === 'switched') {
    return <Trans>Die Produktinstanz läuft jetzt auf Version <b>{versionNr}</b>.</Trans>;
  }
  if (instance.custom) {
    return <Trans>Kundenversion <b>{versionNr}</b> wurde angelegt und der Produktinstanz zugewiesen.</Trans>;
  }
  return <Trans>Offizielle Version <b>{versionNr}</b> wurde angelegt und der Produktinstanz zugewiesen.</Trans>;
}

export function ImportPage(): ReactNode {
  const { has } = useAuth();
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams] = useSearchParams();

  const canImport = has('import.write');

  // `/import?target=product&product=<id>` — SBOM upload onto one product (DEV-107).
  const targetProductId = useMemo<number | null>(() => {
    if (searchParams.get('target') !== 'product') return null;
    const id = Number(searchParams.get('product'));
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams]);
  // `&version=<id>` — opened from a version row: preselect that version.
  const targetVersionId = useMemo<number | null>(() => {
    const id = Number(searchParams.get('version'));
    return targetProductId !== null && Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams, targetProductId]);
  const [productTarget, setProductTarget] = useState<ProductTargetState>({
    productName: null, request: null,
  });
  // `/import?target=instance&instance=<id>` — one delivered instance's scan.
  const targetInstanceId = useMemo<number | null>(() => {
    if (searchParams.get('target') !== 'instance') return null;
    const id = Number(searchParams.get('instance'));
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams]);
  const [instanceLabel, setInstanceLabel] = useState<string | null>(null);
  useEffect(() => {
    if (targetInstanceId === null) return undefined;
    let cancelled = false;
    fetchCmdbCard('ProduktInstanz', targetInstanceId)
      .then((row) => { if (!cancelled) setInstanceLabel(row.label); })
      .catch(() => { if (!cancelled) setInstanceLabel(null); });
    return () => { cancelled = true; };
  }, [targetInstanceId]);
  const targetRequest = useMemo<ImportTargetRequest | null>(
    () => (targetInstanceId !== null
      ? { target: 'instance', instance_id: targetInstanceId }
      : productTarget.request),
    [targetInstanceId, productTarget.request],
  );
  const hasTarget = targetProductId !== null || targetInstanceId !== null;
  const [versionChoice, setVersionChoice] = useState<InstanceVersionChoice | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [plannedAt, setPlannedAt] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<Readonly<Record<string, ImportDecision>>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [planGone, setPlanGone] = useState(false);
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());

  // Keeps the "Plan gültig bis …" hint honest without a per-second timer.
  useEffect(() => {
    if (plannedAt === null) return undefined;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [plannedAt]);

  const runPlan = useCallback(
    async (candidate: File): Promise<void> => {
      setFile(candidate);
      setPlanning(true);
      setPlanError(null);
      setApplyError(null);
      setPlanGone(false);
      setResult(null);
      try {
        const response = await planImport(candidate, targetRequest ?? undefined);
        setPlan(response.plan);
        setFilename(response.filename ?? candidate.name);
        setPlannedAt(Date.now());
        setNow(Date.now());
        setDecisions(initialDecisions(response.plan));
      } catch (err) {
        setPlan(null);
        setPlannedAt(null);
        setDecisions({});
        setPlanError(errorMessage(err));
      } finally {
        setPlanning(false);
      }
    },
    [targetRequest],
  );

  const targetIncomplete = hasTarget && targetRequest === null;

  const onPick = useCallback(
    (candidate: File | undefined): void => {
      if (candidate === undefined || !canImport || targetIncomplete) return;
      void runPlan(candidate);
    },
    [canImport, runPlan, targetIncomplete],
  );

  const setDecision = useCallback((entryId: string, decision: ImportDecision): void => {
    setDecisions((current) => ({ ...current, [entryId]: decision }));
  }, []);

  // "Alle übernehmen/überspringen" covers creates and updates only: removing
  // every component a scan missed, or guessing ambiguous matches, must stay
  // an individual decision.
  const setAll = useCallback((decision: ImportDecision): void => {
    if (plan === null) return;
    const bulk = new Set(
      plan.entries
        .filter((entry) => entry.action === 'create' || entry.action === 'update')
        .filter((entry) => !(plan.requires_decision ?? []).includes(entry.entry_id))
        .map((entry) => entry.entry_id),
    );
    setDecisions((current) => {
      const next: Record<string, ImportDecision> = { ...current };
      for (const key of Object.keys(current)) if (bulk.has(key)) next[key] = decision;
      return next;
    });
  }, [plan]);

  const reset = useCallback((): void => {
    setFile(null);
    setFilename(null);
    setPlan(null);
    setPlannedAt(null);
    setDecisions({});
    setPlanError(null);
    setApplyError(null);
    setPlanGone(false);
    setResult(null);
    setVersionChoice(null);
    if (inputRef.current !== null) inputRef.current.value = '';
  }, []);

  const decisionValues = useMemo<readonly ImportDecision[]>(
    () => Object.values(decisions),
    [decisions],
  );
  const incomingCount = decisionValues.filter(isChange).length;
  const keepCount = decisionValues.length - incomingCount;
  const undecidedCount = (plan?.requires_decision ?? []).filter((id) => decisions[id] === undefined).length;
  // An instance upload can switch the instance's version without a single
  // catalog change; a deviation needs the custom/official choice first.
  const instanceTarget = plan?.target?.kind === 'instance' ? plan.target : null;
  const needsChoice = instanceTarget?.outcome === 'deviation' && versionChoice === null;
  const nothingToApply = instanceTarget !== null
    ? instanceTarget.outcome === 'identical' && incomingCount === 0
    : incomingCount === 0;

  const expiresAt = plannedAt === null ? null : plannedAt + PLAN_TTL_MS;
  const expired = expiresAt !== null && now >= expiresAt;

  const apply = useCallback(async (): Promise<void> => {
    if (plan === null || !canImport) return;
    setApplying(true);
    setApplyError(null);
    setPlanGone(false);
    try {
      // The FULL map for every decidable entry — see the module docstring.
      const response = await applyImport({
        plan_id: plan.plan_id,
        decisions: { ...decisions },
        ...(versionChoice !== null ? { version_choice: versionChoice } : {}),
      });
      setResult(response.result);
      setPlan(null);
      setPlannedAt(null);
      const removed = response.result.removed?.length ?? 0;
      const created = response.result.created.length;
      const updated = response.result.updated.length;
      const errorCount = response.result.errors.length;
      const instanceResult = response.result.instance;
      if (instanceResult?.action === 'switched' || instanceResult?.action === 'created') {
        const versionNr = instanceResult.version_nr ?? '';
        let message: string;
        if (instanceResult.action === 'switched') message = t`Produktinstanz auf Version ${versionNr} umgestellt.`;
        else if (instanceResult.custom) message = t`Kundenversion ${versionNr} angelegt und zugewiesen.`;
        else message = t`Version ${versionNr} angelegt und zugewiesen.`;
        toast.success(message);
      }
      if (errorCount > 0) {
        toast.error(
          t`${plural(errorCount, { one: 'Import mit # Fehler abgeschlossen.', other: 'Import mit # Fehlern abgeschlossen.' })}`,
        );
      } else {
        toast.success(
          removed > 0
            ? t`Import angewendet: ${created} angelegt, ${updated} aktualisiert, ${removed} entfernt.`
            : t`Import angewendet: ${created} angelegt, ${updated} aktualisiert.`,
        );
      }
    } catch (err) {
      setApplyError(errorMessage(err));
      // 404 = the in-process plan expired; offer the way back to the upload.
      if (err instanceof ApiError && err.status === 404) setPlanGone(true);
    } finally {
      setApplying(false);
    }
  }, [plan, canImport, decisions, toast, versionChoice, t]);

  /** Known classes in schema order, then anything else the server sent. */
  const groups = useMemo(() => {
    if (plan === null) return [];
    const known: readonly string[] = IMPORT_KLASS_ORDER;
    const extra = uniqueStrings(plan.entries.map((entry) => entry.klass))
      .filter((klass) => !known.includes(klass))
      .sort((a, b) => a.localeCompare(b, intlLocale()));
    return [...known, ...extra]
      .map((klass) => ({
        klass,
        label: klassLabel(klass),
        entries: plan.entries.filter(
          (entry) =>
            entry.klass === klass && (showUnchanged || entry.action !== 'unchanged'),
        ),
      }))
      .filter((group) => group.entries.length > 0);
    // The class labels are translated: rebuild them on a language switch.
  }, [plan, showUnchanged, locale]);

  // The presentation carries translated field labels: rebuild it on a language switch.
  const presentation = useMemo(() => buildPlanPresentation(plan?.entries ?? []), [plan, locale]);

  const untouchedTotal = useMemo<number>(() => {
    if (plan === null) return 0;
    return Object.values(plan.untouched_in_cmdb).reduce((sum, count) => sum + count, 0);
  }, [plan]);

  const counters = useMemo<readonly KpiCounter[]>(() => {
    if (plan === null) return [];
    return [
      { label: t`Einträge aus Datei`, value: plan.total_rows, tone: 'neutral' },
      { label: t`Neu (+)`, value: plan.creates, tone: plan.creates > 0 ? 'good' : 'neutral' },
      { label: t`Geändert (~)`, value: plan.updates, tone: plan.updates > 0 ? 'warn' : 'neutral' },
      { label: t`Unverändert (=)`, value: plan.unchanged, tone: 'neutral' },
      ...((plan.target?.kind === 'product' && plan.target.mode === 'update') || plan.target?.kind === 'instance'
        ? [{
            label: t`Fehlt im Scan (−)`,
            value: plan.removes ?? 0,
            tone: (plan.removes ?? 0) > 0 ? 'warn' : 'neutral',
            title: t`Komponenten der Stückliste, die die SBOM nicht enthält. Sie bleiben, sofern Sie nicht „Entfernen“ wählen.`,
          } satisfies KpiCounter]
        : []),
      {
        label: t`Nur in CMDB (bleibt)`,
        value: untouchedTotal,
        tone: 'neutral',
        title: t`Einträge im CMDB-Bestand, die in der Datei fehlen. Ein Import löscht nie.`,
      },
    ];
    // The labels are translated: rebuild them on a language switch.
  }, [plan, untouchedTotal, locale]);

  // Defence in depth: the route is already guarded, but a stale bundle whose
  // route table lost the guard must not show an armed upload. Reuses the shared
  // panel so there is one wording of the refusal in the whole SPA.
  if (!canImport) return <PermissionDenied permission="import.write" />;

  // While a finished import is on screen the picker is replaced by the explicit
  // "Weitere Datei importieren" button, so the result cannot be mistaken for a
  // preview of the next file.
  const showDropZone = plan === null && result === null;

  // Plain names for the message placeholders below.
  const fileName = file?.name ?? '';
  const productName = productTarget.productName ?? t`Produkt`;
  const expiryTime = expiresAt !== null ? new Date(expiresAt).toLocaleTimeString(intlLocale()) : '';
  const errorCount = plan?.errors.length ?? 0;
  let pageTitle: string;
  if (targetProductId !== null) pageTitle = t`SBOM für ${productName} hochladen`;
  else if (targetInstanceId !== null && instanceLabel) pageTitle = t`Scan der Produktinstanz ${instanceLabel} hochladen`;
  else if (targetInstanceId !== null) pageTitle = t`Scan der Produktinstanz hochladen`;
  else pageTitle = t`Daten-Import mit Merge-Review`;

  return (
    <>
      <PageHeader
        title={pageTitle}
        sub={
          plan !== null ? (
            <>
              {filename !== null ? <span className="imp-file">{filename}</span> : null}
              {filename !== null && expiresAt !== null ? ' · ' : null}
              {expiresAt !== null ? t`Plan gültig bis ${expiryTime}` : null}
            </>
          ) : undefined
        }
        actions={
          plan !== null ? (
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              <Trans>Abbrechen / neue Datei</Trans>
            </button>
          ) : null
        }
      />

      {targetProductId !== null && result === null ? (
        <ImportTargetPanel
          key={`${targetProductId}:${targetVersionId ?? ''}`}
          productId={targetProductId}
          initialVersionId={targetVersionId}
          disabled={planning || applying || plan !== null}
          onChange={setProductTarget}
        />
      ) : null}

      {showDropZone ? (
        <>
          <button
            type="button"
            className={dragging ? 'drop hover' : 'drop'}
            onClick={() => inputRef.current?.click()}
            disabled={planning || targetIncomplete}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              onPick(event.dataTransfer.files[0]);
            }}
          >
            {/* Spans, not divs: a <button> may only contain phrasing content. */}
            <span className="imp-drop-line">
              {hasTarget ? (
                <Trans><b>CycloneDX-SBOM (JSON) hierher ziehen</b> oder klicken zum Auswählen</Trans>
              ) : (
                <Trans><b>AssetRadar CSV oder CycloneDX-JSON hierher ziehen</b> oder klicken zum Auswählen</Trans>
              )}
            </span>
            <span className="imp-drop-line imp-drop-hint">
              {targetIncomplete
                ? t`Wählen Sie zuerst das Ziel der SBOM.`
                : t`Vor dem Schreiben sehen Sie den Vergleich mit dem CMDB-Bestand.`}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={
              hasTarget
                ? '.json,application/json,application/vnd.cyclonedx+json'
                : '.csv,.json,text/csv,application/json,application/vnd.cyclonedx+json'
            }
            hidden
            onChange={(event) => {
              const picked = event.target.files?.[0];
              // Reset so choosing the same file again fires 'change' again.
              event.target.value = '';
              onPick(picked);
            }}
          />
        </>
      ) : null}

      {planning ? (
        <Banner kind="info">
          <Spinner />{' '}
          {file !== null
            ? t`Vergleiche »${fileName}« mit dem CMDB-Bestand…`
            : t`Vergleiche mit dem CMDB-Bestand…`}
        </Banner>
      ) : null}

      {planError !== null ? (
        <Banner
          kind="error"
          sub={t`Es wurde nichts geschrieben; laden Sie die korrigierte Datei erneut hoch.`}
        >
          <Trans>Plan fehlgeschlagen: {planError}</Trans>
        </Banner>
      ) : null}

      {plan !== null ? (
        <>
          {plan.target !== undefined ? (
            <TargetSummary
              key={plan.plan_id}
              target={plan.target}
              disabled={applying}
              onChoice={setVersionChoice}
            />
          ) : null}
          {undecidedCount > 0 ? (
            <Banner kind="warn" sub={t`Wählen Sie den passenden Katalogeintrag oder legen Sie die Komponente neu an.`}>
              <Plural
                value={undecidedCount}
                one="# Komponente ist nicht eindeutig zuzuordnen."
                other="# Komponenten sind nicht eindeutig zuzuordnen."
              />
            </Banner>
          ) : null}
          {plan.errors.length > 0 ? (
            <Banner
              kind="warn"
              items={plan.errors}
              sub={t`Prüfen Sie die Hinweise vor dem Übernehmen.`}
            >
              <Plural value={errorCount} one="# Hinweis zum Import." other="# Hinweise zum Import." />
            </Banner>
          ) : null}

          {expired ? (
            <Banner
              kind="warn"
              sub={t`Pläne verfallen serverseitig nach 30 Minuten.`}
              items={file !== null ? undefined : [t`Wählen Sie die Datei erneut aus.`]}
            >
              <Trans>Dieser Plan ist wahrscheinlich verfallen.</Trans>{' '}
              {file !== null ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => void runPlan(file)}
                  disabled={planning}
                >
                  <Trans>»{fileName}« erneut vergleichen</Trans>
                </button>
              ) : null}
            </Banner>
          ) : null}

          <KpiRow counters={counters} />

          {untouchedTotal > 0 ? (
            <div className="imp-untouched">
              <span className="imp-untouched-label">
                <Trans>Nur im CMDB vorhanden (bleibt unangetastet):</Trans>
              </span>
              <ChipList>
                {Object.entries(plan.untouched_in_cmdb)
                  .filter(([, count]) => count > 0)
                  .map(([klass, count]) => (
                    <Chip key={klass}>
                      {klassLabel(klass)}: {count}
                    </Chip>
                  ))}
              </ChipList>
            </div>
          ) : null}

          <div className="controls">
            <button
              type="button"
              onClick={() => setAll('incoming')}
              disabled={applying || decisionValues.length === 0}
            >
              <Trans>Alle übernehmen</Trans>
            </button>
            <button
              type="button"
              onClick={() => setAll('existing')}
              disabled={applying || decisionValues.length === 0}
            >
              <Trans>Alle überspringen</Trans>
            </button>
            <label className="check">
              <input
                type="checkbox"
                checked={showUnchanged}
                onChange={(event) => setShowUnchanged(event.target.checked)}
              />
              <Trans>Unveränderte anzeigen</Trans>
            </label>
            <div className="spacer" />
            <span className="muted">{filename ?? ''}</span>
          </div>

          {groups.length === 0 ? (
            <Banner kind="ok">
              <Trans>Keine Unterschiede: Der Dateiinhalt entspricht dem CMDB-Bestand.</Trans>
            </Banner>
          ) : (
            groups.map((group) => (
              <section className="klass-block" key={group.klass}>
                <h2>{group.label}</h2>
                {group.klass === 'ProduktVersionKomponente' ? (
                  <BomReview
                    groups={presentation.bomGroups}
                    decisions={decisions}
                    showUnchanged={showUnchanged}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ) : group.entries.map((entry) => (
                  <PlanEntryItem
                    key={entry.entry_id}
                    entry={entry}
                    presentation={presentation.byEntryId.get(entry.entry_id)}
                    decision={decisions[entry.entry_id] ?? null}
                    decisions={decisions}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ))}
              </section>
            ))
          )}

          {applyError !== null ? (
            <Banner kind="error">
              <Trans>Anwenden fehlgeschlagen: {applyError}</Trans>
              {planGone && file !== null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={() => void runPlan(file)}>
                    <Trans>»{fileName}« erneut hochladen und vergleichen</Trans>
                  </button>
                </div>
              ) : null}
              {planGone && file === null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={reset}>
                    <Trans>Neue Datei auswählen</Trans>
                  </button>
                </div>
              ) : null}
            </Banner>
          ) : null}

          <div className="applybar">
            <button
              type="button"
              className="primary"
              onClick={() => void apply()}
              disabled={applying || nothingToApply || undecidedCount > 0 || needsChoice}
              title={
                undecidedCount > 0
                  ? t`Entscheiden Sie zuerst über die nicht eindeutig zugeordneten Komponenten.`
                  : needsChoice
                    ? t`Wählen Sie, welche Version für die abweichende Konfiguration angelegt wird.`
                    : nothingToApply
                      ? t`Keine Änderung ausgewählt. Mindestens ein Eintrag muss übernommen werden.`
                      : undefined
              }
            >
              {applying ? (
                <Spinner variant="on-accent" label={t`Wende an…`} />
              ) : instanceTarget !== null ? (
                t`Übernehmen`
              ) : (
                t`${plural(incomingCount, { one: 'Import anwenden (# Änderung)', other: 'Import anwenden (# Änderungen)' })}`
              )}
            </button>
            <span className="muted">
              {applying
                ? t`Wende an…`
                : keepCount > 0
                  ? t`${plural(keepCount, { one: '# wird behalten/übersprungen', other: '# werden behalten/übersprungen' })}`
                  : ''}
            </span>
            <div className="spacer" />
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              <Trans>Abbrechen / neue Datei</Trans>
            </button>
          </div>
        </>
      ) : null}

      {result !== null ? (
        <>
          <Banner kind={result.errors.length > 0 ? 'error' : 'ok'}>
            <ResultSummary result={result} />
          </Banner>
          {result.instance !== undefined && result.instance.action !== 'failed' ? (
            <Banner kind="ok">
              <InstanceResultNote instance={result.instance} />
            </Banner>
          ) : null}
          {result.errors.length > 0 ? (
            <Banner kind="error" items={result.errors}>
              <Trans>Diese Einträge wurden nicht geschrieben:</Trans>
            </Banner>
          ) : null}
          <Banner kind="info">
            <Trans>
              Die Änderungen werden an Dependency-Track übertragen; das Dashboard aktualisiert sich nach
              der Analyse.
            </Trans>
          </Banner>
          <div className="row">
            <button type="button" className="primary" onClick={reset}>
              <Trans>Weitere Datei importieren</Trans>
            </button>
            {targetProductId !== null ? (
              <Link className="link-button" to="/stammdaten?klasse=ProduktVersion"><Trans>Zu den Produktversionen</Trans></Link>
            ) : null}
            {targetInstanceId !== null ? (
              <Link className="link-button" to={`/stammdaten?klasse=ProduktInstanz&id=${targetInstanceId}`}>
                <Trans>Zur Produktinstanz</Trans>
              </Link>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

export default ImportPage;
